import { Integration_Names_Enum } from '@main/graphql/types.generated';
import {
  formatZodIssuePath,
  getObjectShape,
  getSchemaFieldPaths,
  unwrapTransparentSchema,
} from '@main/shared/utils';
import z from 'zod/v4';

import type { IntegrationConnector } from '../lib/integration-connector';
import * as allConnectors from './connectors-all';

const registry = new Map<Integration_Names_Enum, IntegrationConnector>();

Object.values(allConnectors).forEach(registerIntegrationConnector);

function registerIntegrationConnector(connector: IntegrationConnector) {
  assertNoDuplicateConnectorCategories(connector);
  assertNoDuplicateConnectorConfigKeys(connector);
  assertUserConfigSchemaKeysArePersisted(connector);
  registry.set(connector.provider, connector);
  return connector;
}

function assertNoDuplicateConnectorCategories(connector: IntegrationConnector) {
  const duplicateCategories = [
    ...new Set(
      connector.categories.filter(
        (category, index, categories) => categories.indexOf(category) !== index,
      ),
    ),
  ];

  if (duplicateCategories.length > 0) {
    throw new Error(
      `Duplicate connector categories in "${connector.provider}" connector: ${duplicateCategories.join(', ')}`,
    );
  }
}

function assertNoDuplicateConnectorConfigKeys(connector: IntegrationConnector) {
  for (const auth of connector.auths.group) {
    const configKeys = new Set(Object.keys(getObjectShape(auth.configSchema)));
    const duplicateKeys = Object.keys(getObjectShape(auth.encryptedConfigSchema)).filter((key) =>
      configKeys.has(key),
    );
    if (duplicateKeys.length > 0) {
      throw new Error(
        `Duplicate connector config keys in "${connector.provider}.${auth.name}" auth: ${duplicateKeys.join(', ')}`,
      );
    }
  }
}

function assertUserConfigSchemaKeysArePersisted(connector: IntegrationConnector) {
  for (const auth of connector.auths.group) {
    const unknownConfigKeys = getUnknownObjectSchemaPaths(
      auth.userConfigSchema.configSchema,
      auth.configSchema,
    );
    const unknownEncryptedConfigKeys = getUnknownObjectSchemaPaths(
      auth.userConfigSchema.encryptedConfigSchema,
      auth.encryptedConfigSchema,
    );

    if (unknownConfigKeys.length > 0 || unknownEncryptedConfigKeys.length > 0) {
      const unknownKeys = [...unknownConfigKeys, ...unknownEncryptedConfigKeys]
        .map(formatZodIssuePath)
        .join(', ');
      throw new Error(
        `User config schema keys must be declared in "${connector.provider}.${auth.name}" auth config schemas: ${unknownKeys}`,
      );
    }
  }
}

function getUnknownObjectSchemaPaths(
  userConfigSchema: z.ZodObject,
  persistedConfigSchema: z.ZodObject,
): string[][] {
  const persistedConfigShape = getObjectShape(persistedConfigSchema);

  return Object.entries(getObjectShape(userConfigSchema)).flatMap(([key, userFieldSchema]) => {
    const persistedFieldSchema = persistedConfigShape[key];
    if (!persistedFieldSchema) {
      return [[key]];
    }

    const unwrappedUserFieldSchema = unwrapTransparentSchema(userFieldSchema);
    const unwrappedPersistedFieldSchema = unwrapTransparentSchema(persistedFieldSchema);
    if (unwrappedUserFieldSchema instanceof z.ZodObject) {
      if (!(unwrappedPersistedFieldSchema instanceof z.ZodObject)) {
        const childPaths = getSchemaFieldPaths(unwrappedUserFieldSchema);
        return childPaths.length === 0 ? [[key]] : childPaths.map((path) => [key, ...path]);
      }
      return getUnknownObjectSchemaPaths(
        unwrappedUserFieldSchema,
        unwrappedPersistedFieldSchema,
      ).map((path) => [key, ...path]);
    }

    return unwrappedPersistedFieldSchema instanceof z.ZodObject ? [[key]] : [];
  });
}

export function getRegisteredIntegrationConnector(providerName: Integration_Names_Enum) {
  const connector = registry.get(providerName);
  if (!connector) {
    throw new Error(`No integration connector registered for provider "${providerName}".`);
  }
  return connector;
}
